import { Icon } from "components/icon/Icon";
import { Button } from "components/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipArrow,
} from "components/ui/tooltip";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "utils";
import { Text } from "components/text";

type VisibilityMode = "always" | "onParentHover";

interface ExpandableInfoTooltipProps {
  text?: string;
  moreText?: string | ReactNode;
  testId?: string;
  side?: "top" | "bottom";
  children?: ReactNode;
  triggerClassName?: string;
  contentClassName?: string;
  align?: "start" | "center" | "end";
  visibilityMode?: VisibilityMode;
}

export const ExpandableInfoTooltip = ({
  text,
  moreText,
  testId,
  triggerClassName = "",
  side = "bottom",
  children,
  contentClassName = "",
  align = "center",
  visibilityMode = "always",
}: ExpandableInfoTooltipProps) => {
  const [openMore, setOpenMore] = useState(false);
  const { t } = useTranslation(["general"]);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "text-secondary-400 hover:text-secondary-500 shrink-0",
            visibilityMode === "onParentHover" &&
              "opacity-0 group-hover:opacity-100 transition-opacity duration-200",
            triggerClassName,
          )}
          data-testid={testId}
        >
          <Icon name="info" size="medium" />
        </span>
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        className={cn(
          "text-left shadow-tooltip text-secondary-600 whitespace-normal z-10 border-none gap-2 py-2 max-w-100",
          contentClassName,
        )}
        sideOffset={6}
      >
        <TooltipArrow
          className="fill-white drop-shadow-xs"
          width={12}
          height={6}
        />
        {children || (
          <p className="flex justify-start items-start text-left w-full mt-2">
            {text}
          </p>
        )}
        <div className="flex justify-start items-start text-left w-full">
          {moreText && (
            <div className="flex flex-col gap-2 items-start">
              {openMore && (
                <Text as="span" variant="caption">
                  {moreText}
                </Text>
              )}
              <Button
                variant="link"
                size="small"
                icon={openMore ? "arrow-up" : "arrow-down"}
                className="p-0"
                onClick={() => setOpenMore(!openMore)}
                label={
                  openMore
                    ? t("general:buttons.learnLess")
                    : t("general:buttons.learnMore")
                }
              />
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
